import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import {
  PatientTargetModel,
  TargetNoteMessageModel,
} from '../../../core/api/models';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { SyncSchedulerService } from '../../../core/services/sync-scheduler.service';
import { SharedModule } from '../../shared.module';

type DisplayTargetNoteMessage = TargetNoteMessageModel & {
  SyncStatus?: 'pending' | 'syncing' | 'failed' | 'synced';
  ShouldMarkAsNew?: boolean;
};

@Component({
  selector: 'app-target-note-thread',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './target-note-thread.component.html',
  styleUrl: './target-note-thread.component.scss',
})
export class TargetNoteThreadComponent implements OnChanges {
  @ViewChild('messageList') messageList?: ElementRef<HTMLDivElement>;

  @Input() target?: PatientTargetModel | null;
  @Input() targetId?: number | null;
  @Input() patientId?: number | null;
  @Input() targetNotes?: string | null;
  @Input() notesLastModifiedByUserFullName?: string | null;
  @Input() notesLastModifiedDate?: string | null;
  @Input() shouldMarkAsNew = true;
  @Input() readOnly = false;
  @Output() latestMessageChanged = new EventEmitter<TargetNoteMessageModel>();

  messages: DisplayTargetNoteMessage[] = [];
  newMessage = '';
  hasMore = false;
  oldestMessageId?: number | null;
  isLoading = false;
  isSending = false;
  isOfflineMode = false;

  private readonly pageSize = 25;

  constructor(
    private staffApiService: StaffApiService,
    private offlineDataService: OfflineDataService,
    private syncSchedulerService: SyncSchedulerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['target'] || changes['targetId']) {
      this.loadMessages();
    }
  }

  loadMessages(): void {
    const targetId = this.resolvedTargetId();
    if (!targetId) {
      this.messages = [];
      return;
    }

    this.isOfflineMode = this.offlineDataService.isInOfflineMode();
    if (this.isOfflineMode) {
      this.loadOfflineMessages();
      return;
    }

    this.isLoading = true;
    this.staffApiService
      .GetTargetNoteMessages({ targetId, pageSize: this.pageSize })
      .pipe(
        catchError((error) => {
          console.error('Error loading target note messages', error);
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((page) => {
        this.messages = page?.Messages ?? [];
        this.hasMore = !!page?.HasMore;
        this.oldestMessageId = page?.OldestMessageID;
        this.addSummaryMessageIfNeeded();
        this.scrollToNewestMessage();
      });
  }

  loadEarlierMessages(): void {
    const targetId = this.resolvedTargetId();
    if (!targetId || !this.oldestMessageId || this.isOfflineMode) {
      return;
    }

    const previousScrollHeight = this.messageList?.nativeElement.scrollHeight ?? 0;
    this.isLoading = true;
    this.staffApiService
      .GetTargetNoteMessages({
        targetId,
        beforeMessageId: this.oldestMessageId,
        pageSize: this.pageSize,
      })
      .pipe(
        catchError((error) => {
          console.error('Error loading earlier target note messages', error);
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((page) => {
        const earlierMessages = page?.Messages ?? [];
        this.messages = [...earlierMessages, ...this.messages];
        this.hasMore = !!page?.HasMore;
        this.oldestMessageId = page?.OldestMessageID ?? this.oldestMessageId;
        setTimeout(() => {
          const messageList = this.messageList?.nativeElement;
          if (messageList) {
            messageList.scrollTop = messageList.scrollHeight - previousScrollHeight;
          }
        });
      });
  }

  sendMessage(): void {
    const message = this.newMessage.trim();
    const targetId = this.resolvedTargetId();
    if (!message || !targetId || this.readOnly) {
      return;
    }

    this.isOfflineMode = this.offlineDataService.isInOfflineMode();
    if (this.isOfflineMode) {
      this.sendOfflineMessage(targetId, message);
      return;
    }

    this.isSending = true;
    this.staffApiService
      .AddTargetNoteMessage({
        body: {
          PatientTargetID: targetId,
          Message: message,
          ClientMessageKey: this.createGuid(),
          ClientCreatedDate: new Date().toISOString(),
          ShouldMarkAsNew: this.shouldMarkAsNew,
        },
      })
      .pipe(
        catchError((error) => {
          console.error('Error sending target note message', error);
          return of(null);
        }),
        finalize(() => {
          this.isSending = false;
        })
      )
      .subscribe((savedMessage) => {
        if (!savedMessage) {
          return;
        }

        this.messages = [...this.messages, savedMessage];
        this.newMessage = '';
        this.updateTargetSummary(savedMessage);
        this.latestMessageChanged.emit(savedMessage);
        this.scrollToNewestMessage();
      });
  }

  messageClass(message: DisplayTargetNoteMessage): string {
    if (message.SyncStatus === 'pending' || message.SyncStatus === 'syncing') {
      return 'target-note-message-current target-note-message-pending';
    }

    if (message.IsCurrentUser) {
      return 'target-note-message-current';
    }

    const paletteIndex = Math.abs(message.CreatedByUserID ?? 0) % 4;
    return `target-note-message-other target-note-message-sender-${paletteIndex}`;
  }

  statusText(message: DisplayTargetNoteMessage): string {
    if (message.SyncStatus === 'pending') {
      return 'Pending';
    }

    if (message.SyncStatus === 'syncing') {
      return 'Syncing';
    }

    if (message.SyncStatus === 'failed') {
      return 'Failed';
    }

    return '';
  }

  trackByMessage(index: number, message: DisplayTargetNoteMessage): string | number {
    return message.ID ?? message.ClientMessageKey ?? index;
  }

  private sendOfflineMessage(targetId: number, message: string): void {
    const patientId = this.resolvedPatientId();
    if (!patientId) {
      return;
    }

    this.offlineDataService
      .saveOfflineTargetNoteMessage(
        patientId,
        targetId,
        message,
        this.shouldMarkAsNew
      )
      .subscribe((queuedMessage) => {
        this.messages = [...this.messages, queuedMessage];
        this.newMessage = '';
        this.updateTargetSummary(queuedMessage);
        this.latestMessageChanged.emit(queuedMessage);
        this.syncSchedulerService.markDirty('target note message queued');
        this.scrollToNewestMessage();
      });
  }

  private loadOfflineMessages(): void {
    const patientId = this.resolvedPatientId();
    const targetId = this.resolvedTargetId();
    if (!patientId || !targetId) {
      this.messages = [];
      return;
    }

    this.messages = this.offlineDataService.getOfflineTargetNoteMessages(
      patientId,
      targetId
    );
    this.hasMore = false;
    this.oldestMessageId = this.messages[0]?.ID;
    this.addSummaryMessageIfNeeded();
    this.scrollToNewestMessage();
  }

  private addSummaryMessageIfNeeded(): void {
    const summary = this.resolvedTargetNotes()?.trim();
    if (!summary || this.messages.length > 0) {
      return;
    }

    this.messages = [
      {
        ID: 0,
        PatientTargetID: this.resolvedTargetId() ?? 0,
        Message: summary,
        CreatedByUserFullName:
          this.target?.NotesLastModifiedByUserFullName ??
          this.notesLastModifiedByUserFullName ??
          'Unknown user',
        CreatedDate:
          this.target?.NotesLastModifiedDate ??
          this.notesLastModifiedDate ??
          new Date().toISOString(),
        IsCurrentUser: false,
      },
    ];
  }

  private updateTargetSummary(message: TargetNoteMessageModel): void {
    if (!this.target) {
      return;
    }

    this.target.TargetNotes = message.Message;
    this.target.NotesLastModifiedByUserFullName =
      message.CreatedByUserFullName ?? this.target.NotesLastModifiedByUserFullName;
    this.target.NotesLastModifiedDate =
      message.CreatedDate ?? this.target.NotesLastModifiedDate;
    this.target.IsTargetNoteNew = this.shouldMarkAsNew;
  }

  private resolvedTargetId(): number | null {
    return this.target?.ID ?? this.targetId ?? null;
  }

  private resolvedPatientId(): number | null {
    return this.target?.PatientID ?? this.patientId ?? null;
  }

  private resolvedTargetNotes(): string | null | undefined {
    return this.target?.TargetNotes ?? this.targetNotes;
  }

  private scrollToNewestMessage(): void {
    setTimeout(() => {
      const messageList = this.messageList?.nativeElement;
      if (messageList) {
        messageList.scrollTop = messageList.scrollHeight;
      }
    });
  }

  private createGuid(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (value) => {
      const random = (Math.random() * 16) | 0;
      const replacement = value === 'x' ? random : (random & 0x3) | 0x8;
      return replacement.toString(16);
    });
  }
}
